import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Globe, Phone, Search, Brain, Shield, FileText, TrendingUp, Zap, BarChart2,
  AlertTriangle, CheckCircle, XCircle, ChevronRight,
  Target, Layers, Eye, Users, Award, MapPin, Mail, Link2, Calendar,
  ArrowRight, Clock, Flame, Lock, Crown, Lightbulb, Star, Info, Code2,
  Copy, Check, ChevronDown, ChevronUp, Gauge, Unlink, ShieldAlert, ShieldCheck
} from 'lucide-react';
import { FixesPanel, ActionPlan as ActionPlanSection, MarketIntelligenceSection, GeoVisibilitySection } from "@/components/ActionPlan";
import { useTranslations, translateSeverity, translateCategory } from "@/lib/translations";
import { toast } from "sonner";

// ─── PREMIUM COLOR PALETTE ──────────────────────────────────────────────────

const fmtNum = (v) => {
  if (v == null || v === 'N/A') return 'N/A';
  if (typeof v !== 'number') return v;
  if (Number.isInteger(v)) return v;
  return parseFloat(v.toFixed(1));
};

const WORD_NUMS = {
  zero:0,one:1,two:2,three:3,four:4,five:5,six:6,seven:7,eight:8,nine:9,ten:10,
  eleven:11,twelve:12,thirteen:13,fourteen:14,fifteen:15,sixteen:16,seventeen:17,
  eighteen:18,nineteen:19,twenty:20,thirty:30,forty:40,fifty:50,sixty:60,
  seventy:70,eighty:80,ninety:90,hundred:100
};
const parseScore = (v) => {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const n = Number(v);
    if (!isNaN(n)) return Math.round(n);
    const parts = v.toLowerCase().replace(/-/g, ' ').trim().split(/\s+/);
    let total = 0, found = false;
    for (const p of parts) { if (WORD_NUMS[p] !== undefined) { total += WORD_NUMS[p]; found = true; } }
    if (found && total >= 0 && total <= 100) return total;
  }
  return 0;
};

const COLORS = {
  overview: '#3B82F6',
  seo: '#3B82F6',
  ai_readiness: '#8B5CF6',
  trust: '#14B8A6',
  content: '#F59E0B',
  conversion: '#EF4444',
  indexing: '#F97316',
  contact: '#22C55E',
  coverage: '#06B6D4',
};

// ─── helpers ────────────────────────────────────────────────────────────────

function scoreColorHex(score) {
  if (score >= 75) return '#22C55E';
  if (score >= 50) return '#F59E0B';
  if (score >= 25) return '#F97316';
  return '#EF4444';
}

function ScoreGauge({ score: rawScore, label, icon: Icon, accentColor, className = '' }) {
  const score = parseScore(rawScore);
  const color = accentColor || scoreColorHex(score);
  
  return (
    <div 
      className={`flex flex-col items-center p-3 sm:p-5 rounded-xl border ${className}`}
      style={{ 
        background: '#151C2B',
        borderColor: 'rgba(255,255,255,0.04)'
      }}
    >
      {Icon && (
        <div 
          className="w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center mb-1 sm:mb-2"
          style={{ background: `${color}20` }}
        >
          <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" style={{ color }} />
        </div>
      )}
      
      <div className="text-xl sm:text-2xl font-semibold leading-none" style={{ color }}>
        {score}
      </div>
      
      <div className="text-[9px] sm:text-[10px] text-[#9CA3AF] uppercase tracking-wider mt-1 text-center font-medium">
        {label}
      </div>
      
      <div className="w-full mt-2 h-1 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
        <div className="h-full rounded-full" style={{ width: `${score}%`, background: color }} />
      </div>
    </div>
  );
}

function SectionHeader({ icon: Icon, title, score, accentColor = '#E5E7EB' }) {
  return (
    <div className="flex flex-row items-center justify-between gap-4 mb-6">
      <h3 
        className="text-lg sm:text-xl font-semibold uppercase tracking-wider flex items-center gap-2 sm:gap-3"
        style={{ color: '#E5E7EB', letterSpacing: '1px' }}
      >
        {Icon && <Icon className="w-5 h-5 shrink-0" style={{ color: accentColor }} />} 
        {title}
      </h3>
      {score !== undefined && (
        <span style={{ color: accentColor }} className="text-xl font-semibold shrink-0">
          {score}<span className="text-[#9CA3AF] text-sm ml-1">/100</span>
        </span>
      )}
    </div>
  );
}

function TextBlock({ label, value, className = '' }) {
  if (!value) return null;
  return (
    <div className={className}>
      {label && <div className="text-xs sm:text-sm text-[#9CA3AF] uppercase tracking-wider mb-2">{label}</div>}
      <p className="text-[15px] sm:text-[16px] text-[#D1D5DB] leading-relaxed break-words whitespace-pre-line">{value}</p>
    </div>
  );
}

function ImpactBadge({ level }) {
  const styles = {
    High: { bg: 'rgba(239,68,68,0.15)', color: '#EF4444', border: 'rgba(239,68,68,0.3)' },
    Medium: { bg: 'rgba(245,158,11,0.15)', color: '#F59E0B', border: 'rgba(245,158,11,0.3)' },
    Low: { bg: 'rgba(59,130,246,0.15)', color: '#3B82F6', border: 'rgba(59,130,246,0.3)' },
  };
  const style = styles[level] || styles.Medium;
  return (
    <span 
      className="text-[10px] sm:text-[11px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-full shrink-0"
      style={{ background: style.bg, color: style.color, border: `1px solid ${style.border}` }}
    >
      Impact: {level}
    </span>
  );
}

function EffortBadge({ level }) {
  const styles = {
    Low: { bg: 'rgba(34,197,94,0.15)', color: '#22C55E', border: 'rgba(34,197,94,0.3)' },
    Medium: { bg: 'rgba(245,158,11,0.15)', color: '#F59E0B', border: 'rgba(245,158,11,0.3)' },
    High: { bg: 'rgba(239,68,68,0.15)', color: '#EF4444', border: 'rgba(239,68,68,0.3)' },
  };
  const style = styles[level] || styles.Medium;
  return (
    <span 
      className="text-[10px] sm:text-[11px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-full shrink-0"
      style={{ background: style.bg, color: style.color, border: `1px solid ${style.border}` }}
    >
      Effort: {level}
    </span>
  );
}

function PremiumCard({ children, className = '', glowColor = null }) {
  return (
    <Card 
      className={`rounded-[20px] transition-all duration-300 hover:shadow-xl ${className}`}
      style={{ 
        background: '#111826',
        border: '1px solid rgba(255,255,255,0.04)',
        boxShadow: glowColor 
          ? `inset 0 0 0 1px rgba(255,255,255,0.04), 0 0 60px -20px ${glowColor}30`
          : 'inset 0 0 0 1px rgba(255,255,255,0.04)'
      }}
    >
      {children}
    </Card>
  );
}

function ListItem({ children, icon: Icon, color = '#9CA3AF' }) {
  return (
    <div className="flex items-start gap-3 text-[15px] sm:text-[16px] text-[#D1D5DB]">
      {Icon && <Icon className="w-4 h-4 shrink-0 mt-0.5" style={{ color }} />}
      <span className="break-words">{children}</span>
    </div>
  );
}

// ─── Problem Fix Display (gated behind button + paywall) ─────────────────────

function ProblemFixDisplay({ fix, canViewCodeFixes, t }) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!fix) return null;

  const isCode = /(<\?xml|<script|<urlset|<link\s|<meta\s|"@context"|"@type"|<\/|xmlns|<url>|<loc>)/i.test(fix);

  if (!canViewCodeFixes) {
    return (
      <div
        className="mt-3 p-3 rounded-lg flex flex-col sm:flex-row items-start sm:items-center gap-3"
        style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.15)' }}
        data-testid="fix-paywall-inline"
      >
        <div className="flex items-center gap-2 flex-1">
          <Lock className="w-4 h-4 text-amber-400 shrink-0" />
          <span className="text-sm text-zinc-300">
            <span className="font-semibold text-amber-400">{t('fix_available')}</span> — {t('unlock_with_paid')}
          </span>
        </div>
        <Link to="/pricing">
          <Button
            size="sm"
            className="text-white font-medium text-xs px-3 py-1.5 rounded-lg"
            style={{ background: 'linear-gradient(135deg, #F59E0B, #D97706)' }}
            data-testid="fix-unlock-btn"
          >
            <Crown className="w-3 h-3 mr-1" /> {t('unlock')}
          </Button>
        </Link>
      </div>
    );
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(fix).then(() => {
      setCopied(true);
      toast.success('Copied!');
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="mt-3">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-lg transition-all hover:brightness-110"
        style={{
          background: 'rgba(59,130,246,0.15)',
          border: '1px solid rgba(59,130,246,0.25)',
          color: '#93C5FD',
        }}
        data-testid="problem-fix-toggle"
      >
        <Code2 className="w-3 h-3" />
        {expanded ? t('hide_fix') : t('show_fix')}
        {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>
      {expanded && (
        <div className="mt-2">
          {isCode ? (
            <div className="relative rounded-lg overflow-hidden" style={{ background: '#0D1117' }}>
              <div className="flex items-center justify-between px-4 py-2 border-b border-white/10">
                <span className="text-[10px] text-zinc-500 uppercase tracking-widest font-mono">{t('code_label')}</span>
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-1 text-[11px] text-zinc-400 hover:text-white transition-colors"
                  data-testid="problem-fix-copy"
                >
                  {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? t('copied') : t('copy')}
                </button>
              </div>
              <pre
                className="p-4 text-[11px] leading-relaxed text-zinc-300 overflow-x-auto"
                style={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
              >
                {fix}
              </pre>
            </div>
          ) : (
            <div className="p-3 rounded-lg bg-white/[0.03] border border-white/5 flex items-start justify-between gap-2">
              <p className="text-sm text-[#D1D5DB] flex-1">{fix}</p>
              <button
                onClick={handleCopy}
                className="flex items-center gap-1 text-[10px] text-zinc-400 hover:text-white transition-colors shrink-0"
                data-testid="problem-fix-copy"
              >
                {copied ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
                {copied ? t('copied') : t('copy')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── main component ──────────────────────────────────────────────────────────

// ─── SECURITY SECTION ────────────────────────────────────────────────────────
const SEV_STYLE = {
  high: { color: '#EF4444', bg: 'rgba(239,68,68,0.10)', label: 'High' },
  medium: { color: '#F59E0B', bg: 'rgba(245,158,11,0.10)', label: 'Medium' },
  low: { color: '#3B82F6', bg: 'rgba(59,130,246,0.10)', label: 'Low' },
  info: { color: '#9CA3AF', bg: 'rgba(156,163,175,0.10)', label: 'Info' },
};

function StatusIcon({ status }) {
  if (status === 'pass') return <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />;
  if (status === 'fail') return <XCircle className="w-4 h-4 text-red-400 shrink-0" />;
  if (status === 'warn') return <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />;
  return <Info className="w-4 h-4 text-zinc-400 shrink-0" />;
}

function SecuritySection({ security, t }) {
  if (!security || typeof security.score !== 'number') return null;
  const { score, grade, checks = [], issues = [], ssl = {}, counts = {} } = security;
  const accent = score >= 75 ? '#22C55E' : score >= 50 ? '#F59E0B' : '#EF4444';

  return (
    <PremiumCard glowColor={accent}>
      <CardContent className="p-4 sm:p-10">
        <SectionHeader icon={Shield} title={t('security', 'Security')} score={score} accentColor={accent} />

        {/* summary badges */}
        <div className="flex flex-wrap gap-3 mb-6">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold" style={{ background: `${accent}18`, color: accent }}>
            <ShieldCheck className="w-4 h-4" /> {t('security_grade', 'Grade')} {grade}
          </span>
          {ssl?.days_remaining != null && (
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm text-zinc-300" style={{ background: 'rgba(255,255,255,0.05)' }}>
              <Lock className="w-4 h-4 text-emerald-400" /> {t('ssl_expires_in', 'SSL expires in')} {ssl.days_remaining} {t('days', 'days')}
            </span>
          )}
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm text-zinc-300" style={{ background: 'rgba(255,255,255,0.05)' }}>
            {counts.pass || 0} ✓ · {counts.warn || 0} ⚠ · {counts.fail || 0} ✕
          </span>
        </div>

        {/* checks grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6">
          {checks.map((c, i) => (
            <div key={i} className="flex items-start gap-2 p-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)' }}>
              <StatusIcon status={c.status} />
              <div className="min-w-0">
                <p className="text-sm text-white font-medium">{c.label}</p>
                <p className="text-xs text-zinc-400 break-words">{c.detail}</p>
              </div>
            </div>
          ))}
        </div>

        {/* prioritized issues + fixes */}
        {issues.length > 0 && (
          <div>
            <div className="text-xs text-[#9CA3AF] uppercase tracking-wider mb-3 flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-amber-400" /> {t('security_issues', 'Security issues to fix')}
            </div>
            <div className="space-y-3">
              {issues.map((iss, i) => {
                const st = SEV_STYLE[iss.severity] || SEV_STYLE.low;
                return (
                  <div key={i} className="p-4 rounded-xl" style={{ background: st.bg, border: `1px solid ${st.color}30` }}>
                    <div className="flex items-center justify-between gap-3 mb-1">
                      <p className="text-sm font-semibold text-white break-words">{iss.title}</p>
                      <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded shrink-0" style={{ background: `${st.color}25`, color: st.color }}>{st.label}</span>
                    </div>
                    <p className="text-xs text-zinc-300 mb-2">{iss.why}</p>
                    <p className="text-xs text-emerald-300/90 font-mono break-words"><span className="text-zinc-500">{t('fix', 'Fix')}: </span>{iss.fix}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </PremiumCard>
  );
}

// ─── CORE WEB VITALS SECTION ────────────────────────────────────────────────
function CwvMetric({ label, value, unit, rating }) {
  const ratingColor = { good: '#22C55E', 'needs-improvement': '#F59E0B', poor: '#EF4444' }[rating] || '#9CA3AF';
  return (
    <div className="p-4 rounded-xl text-center" style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${ratingColor}30` }}>
      <p className="text-xs text-zinc-400 uppercase tracking-wide mb-1">{label}</p>
      <p className="text-2xl font-bold" style={{ color: ratingColor }}>{value}<span className="text-sm text-zinc-500 ml-0.5">{unit}</span></p>
      {rating && rating !== 'unknown' && (
        <p className="text-[10px] uppercase mt-1" style={{ color: ratingColor }}>{rating.replace('-', ' ')}</p>
      )}
    </div>
  );
}

function CoreWebVitalsSection({ cwv, t }) {
  if (!cwv || !cwv.available) return null;
  const a = cwv.assessment || {};
  const accent = (cwv.performance_score ?? 0) >= 90 ? '#22C55E' : (cwv.performance_score ?? 0) >= 50 ? '#F59E0B' : '#EF4444';
  return (
    <PremiumCard glowColor={accent}>
      <CardContent className="p-4 sm:p-10">
        <SectionHeader icon={Gauge} title={t('core_web_vitals', 'Core Web Vitals')} score={cwv.performance_score} accentColor={accent} />
        <p className="text-sm text-zinc-400 mb-5">{t('cwv_desc', 'Real-world loading & interactivity performance measured by Google PageSpeed (mobile).')}</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <CwvMetric label="LCP" value={cwv.lcp_ms != null ? (cwv.lcp_ms / 1000).toFixed(2) : '—'} unit="s" rating={a.lcp} />
          <CwvMetric label="CLS" value={cwv.cls != null ? cwv.cls.toFixed(3) : '—'} unit="" rating={a.cls} />
          <CwvMetric label="INP" value={cwv.inp_ms != null ? Math.round(cwv.inp_ms) : '—'} unit="ms" rating={a.inp} />
          <CwvMetric label={t('performance', 'Performance')} value={cwv.performance_score ?? '—'} unit="" rating={(cwv.performance_score ?? 0) >= 90 ? 'good' : (cwv.performance_score ?? 0) >= 50 ? 'needs-improvement' : 'poor'} />
        </div>
      </CardContent>
    </PremiumCard>
  );
}

// ─── BROKEN LINKS SECTION ────────────────────────────────────────────────────
function BrokenLinksSection({ technical, t }) {
  const bl = technical?.broken_links;
  if (!bl || !bl.has_broken_links) return null;
  return (
    <PremiumCard glowColor="#EF4444">
      <CardContent className="p-4 sm:p-10">
        <SectionHeader icon={Unlink} title={t('broken_links', 'Broken Links')} score={undefined} accentColor="#EF4444" />
        <p className="text-sm text-zinc-300 mb-4">
          {t('broken_links_found', 'We found')} <span className="font-bold text-red-400">{bl.broken_links_count}</span> {t('broken_links_pages', 'broken URL(s)')} ({bl.broken_percent}% {t('of_scanned', 'of scanned pages')}).
          {bl.server_error_count > 0 && ` ${bl.server_error_count} ${t('server_errors', 'server error(s) (5xx)')}.`}
        </p>
        <div className="space-y-1.5">
          {bl.examples.map((e, i) => (
            <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg text-xs" style={{ background: 'rgba(239,68,68,0.06)' }}>
              <span className="font-mono font-bold text-red-400 shrink-0">{e.status}</span>
              <span className="font-mono text-zinc-300 truncate">{e.url}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </PremiumCard>
  );
}

export default function BusinessReport({ report, domain, competitive, isActionPlanGated = false, crawledUrls = [], auditId = null, canViewCodeFixes = false, language = 'en' }) {
  const t = useTranslations(language);
  if (!report) return null;

  const { 
    scores = {}, 
    kpi = {},
    overview = {}, 
    contact = {}, 
    coverage = {}, 
    content = {}, 
    trust = {}, 
    seo = {}, 
    indexing = {}, 
    ai_readiness = {}, 
    actions = {},
    executive_summary = '',
    problems_and_fixes = [],
    market_intelligence = null,
    detailed_actions: miActions = [],
    ai_visibility_check: geoCheck = null,
    action_plan: actionPlan = null,
    security = null,
    core_web_vitals: coreWebVitals = null,
    technical = null,
  } = report;

  const hasError = !!report._error && report._error !== null;

  return (
    <div 
      className="space-y-1 overflow-hidden py-4 sm:py-8"
      style={{ 
        background: 'radial-gradient(circle at 20% 10%, #1A2233 0%, #0B0F14 60%)',
        minHeight: '100%'
      }}
    >

      {/* Error banner */}
      {hasError && (
        <PremiumCard className="mx-0">
          <CardContent className="p-4 sm:p-5 flex items-start gap-4" style={{ background: 'rgba(239,68,68,0.1)' }}>
            <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-red-300">Business analysis incomplete</p>
              <p className="text-xs text-red-400/80 mt-1">{report._error}</p>
            </div>
          </CardContent>
        </PremiumCard>
      )}

      {/* AI sampling note */}
      {report.ai_sampling_note && (
        <PremiumCard className="mx-0">
          <CardContent className="p-4 sm:p-5 flex items-start gap-3" style={{ background: 'rgba(59,130,246,0.08)' }}>
            <Info className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
            <p className="text-sm text-blue-200">{report.ai_sampling_note}</p>
          </CardContent>
        </PremiumCard>
      )}

      {/* ── KPI GRID ── */}
      {/* Force 2 columns on mobile */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
        <ScoreGauge score={scores.overview_score || 0} label={t('business_overview', 'Overview')} icon={Globe} accentColor={COLORS.overview} />
        <ScoreGauge score={scores.seo_score || 0} label={t('seo', 'SEO')} icon={Search} accentColor={COLORS.seo} />
        <ScoreGauge score={scores.ai_readiness_score || 0} label={t('ai_readiness', 'AI Readiness')} icon={Brain} accentColor={COLORS.ai_readiness} />
        <ScoreGauge score={scores.trust_score || 0} label={t('trust', 'Trust')} icon={Shield} accentColor={COLORS.trust} />
        <ScoreGauge score={scores.content_score || 0} label={t('content', 'Content')} icon={FileText} accentColor={COLORS.content} />
        <ScoreGauge score={scores.indexing_score || 0} label={t('indexing', 'Indexing')} icon={BarChart2} accentColor={COLORS.indexing} />
        <ScoreGauge score={scores.contact_score || 0} label={t('contact', 'Contact')} icon={Phone} accentColor={COLORS.contact} />
        <ScoreGauge score={scores.coverage_score || 0} label={t('coverage', 'Coverage')} icon={Layers} accentColor={COLORS.coverage} />
        <ScoreGauge score={scores.conversion_risk_score || 0} label={t('conversion_risk', 'Conversion Risk')} icon={Target} accentColor={COLORS.conversion} />
      </div>

      {/* ── EXECUTIVE SUMMARY ── */}
      {executive_summary && (
        <PremiumCard glowColor={COLORS.overview}>
          <CardContent className="p-4 sm:p-10">
            <SectionHeader icon={Lightbulb} title={t('executive_summary')} accentColor={COLORS.overview} />
            <p className="text-[15px] sm:text-[16px] text-[#D1D5DB] leading-relaxed whitespace-pre-line break-words">{executive_summary}</p>
          </CardContent>
        </PremiumCard>
      )}

      {/* ── SECURITY ── */}
      <SecuritySection security={security} t={t} />

      {/* ── CORE WEB VITALS ── */}
      <CoreWebVitalsSection cwv={coreWebVitals} t={t} />

      {/* ── BROKEN LINKS ── */}
      <BrokenLinksSection technical={technical} t={t} />

      {/* ── OVERVIEW ── */}
      <PremiumCard glowColor={COLORS.overview}>
        <CardContent className="p-4 sm:p-10">
          <SectionHeader icon={Globe} title={t('business_overview')} score={overview.score} accentColor={COLORS.overview} />
          
          {overview.site_type && (
            <div className="mb-6">
              <span 
                className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium"
                style={{ background: `${COLORS.overview}15`, color: COLORS.overview }}
              >
                {overview.site_type}
              </span>
            </div>
          )}

          <div className="space-y-6 mb-8">
            <TextBlock label={t('primary_goal')} value={overview.primary_goal} />
            <TextBlock label={t('business_model')} value={overview.business_model} />
            {overview.score_reasoning && (
              <div className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)' }}>
                <TextBlock label={t('score_reasoning')} value={overview.score_reasoning} />
              </div>
            )}
            <TextBlock label={t('overall_assessment')} value={overview.overall_assessment} />
          </div>

          {/* Top Performing Pages */}
          {overview.top_performing_pages?.length > 0 && (
            <div className="mb-8">
              <div className="text-xs text-[#9CA3AF] uppercase tracking-wider mb-4 flex items-center gap-2">
                <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                {t('top_performing_pages')}
              </div>
              <div className="space-y-3">
                {overview.top_performing_pages.map((p, i) => (
                  <div 
                    key={i} 
                    className="p-4 rounded-xl"
                    style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.15)' }}
                  >
                    <p className="text-sm font-mono text-emerald-400 mb-2 truncate max-w-full">{p.url}</p>
                    <p className="text-[15px] sm:text-[16px] text-[#D1D5DB] break-words">{p.why_strong}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Problem Pages */}
        </CardContent>
      </PremiumCard>

      {/* ── CONTACT ── */}
      <PremiumCard glowColor={COLORS.contact}>
        <CardContent className="p-4 sm:p-10">
          <SectionHeader icon={Phone} title={t('contact')} score={contact.score} accentColor={COLORS.contact} />
          
          <div className="space-y-6">
            {/* Detected Elements - structured display for emails and phones */}
            <div>
              <div className="text-xs text-[#9CA3AF] uppercase tracking-wider mb-3">{t('detected_elements')}</div>
              {(contact.emails?.length > 0 || contact.phones?.length > 0) ? (
                <div className="flex flex-col md:flex-row md:justify-between items-start gap-6 md:gap-8">
                  {/* Emails - left aligned */}
                  <div className="flex-1 w-full">
                    {contact.emails?.length > 0 && (
                      <div className="space-y-2">
                        <div className="text-xs text-zinc-500 mb-1">{t('email_label')}</div>
                        {contact.emails.map((email, i) => (
                          <div key={i} className="text-white text-sm flex items-center gap-2 break-all">
                            <Mail className="w-3.5 h-3.5 text-green-400 shrink-0" />
                            <a href={`mailto:${email}`} className="hover:text-green-400 transition-colors">{email}</a>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Social profiles */}
                    {contact.social?.length > 0 && (
                      <div className="mt-4 space-y-2">
                        <div className="text-xs text-zinc-500 mb-1">{t('social_label')}</div>
                        {contact.social.slice(0, 3).map((url, i) => (
                          <div key={i} className="text-white text-sm flex items-center gap-2 break-all">
                            <Globe className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                            <a href={url} target="_blank" rel="noopener noreferrer" className="hover:text-blue-400 transition-colors truncate">{url.replace(/^https?:\/\//, '')}</a>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Contact form */}
                    {contact.contact_form && (
                      <div className="mt-4 text-white text-sm flex items-center gap-2">
                        <CheckCircle className="w-3.5 h-3.5 text-green-400 shrink-0" />
                        <span>{t('contact_form')}</span>
                      </div>
                    )}
                    {/* Addresses */}
                    {contact.addresses?.length > 0 && (
                      <div className="mt-4 space-y-2">
                        <div className="text-xs text-zinc-500 mb-1">{t('address_label')}</div>
                        {contact.addresses.map((addr, i) => (
                          <div key={i} className="text-white text-sm flex items-start gap-2 break-words">
                            <MapPin className="w-3.5 h-3.5 text-green-400 shrink-0 mt-0.5" />
                            <span>{addr}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Opening hours */}
                    {contact.opening_hours?.length > 0 && (
                      <div className="mt-4 space-y-2">
                        <div className="text-xs text-zinc-500 mb-1">{t('opening_hours')}</div>
                        {contact.opening_hours.map((h, i) => (
                          <div key={i} className="text-white/80 text-sm flex items-center gap-2">
                            <Clock className="w-3.5 h-3.5 text-green-400 shrink-0" />
                            <span>{h}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Phones - right aligned on desktop, left on mobile */}
                  {contact.phones?.length > 0 && (
                    <div className="w-full md:w-auto md:text-right">
                      <div className="text-xs text-zinc-500 mb-1">{t('phone_label')}</div>
                      <div className="space-y-2">
                        {contact.phones.map((phone, i) => (
                          <div key={i} className="text-white text-sm flex items-center md:justify-end gap-2">
                            <div className="flex md:hidden items-center gap-2">
                                <Phone className="w-3.5 h-3.5 text-green-400 shrink-0" />
                                <a href={`tel:${phone}`} className="hover:text-green-400 transition-colors">{phone}</a>
                            </div>
                            <div className="hidden md:flex items-center gap-2">
                                <a href={`tel:${phone}`} className="hover:text-green-400 transition-colors">{phone}</a>
                                <Phone className="w-3.5 h-3.5 text-green-400 shrink-0" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                /* Fallback to detected_elements string if no structured data */
                <p className="text-white/90 leading-relaxed break-words">{contact.detected_elements || 'No contact information detected'}</p>
              )}
            </div>
            
            <TextBlock label={t('business_risk')} value={contact.business_risk} />
          </div>

          {(contact.missing_elements_count || contact.missing_elements?.length) ? (
            <div className="mt-6 p-4 rounded-xl flex items-center gap-3" style={{ background: 'rgba(239,68,68,0.05)' }}>
              <AlertTriangle className="w-5 h-5 text-red-400 shrink-0" />
              <span className="text-sm text-[#D1D5DB]">
                {contact.missing_elements_count ?? contact.missing_elements?.length} {t('missing_contact')}.
                {contact.section_note ? ` ${contact.section_note}` : ''}
              </span>
            </div>
          ) : null}
        </CardContent>
      </PremiumCard>

      {/* ── COVERAGE ── */}
      <PremiumCard glowColor={COLORS.coverage}>
        <CardContent className="p-4 sm:p-10">
          <SectionHeader icon={Layers} title={t('coverage')} score={coverage.score} accentColor={COLORS.coverage} />
          
          <div className="space-y-6">
            {coverage.pages_scanned && (
              <div className="flex items-center gap-3">
                <div className="text-3xl font-bold" style={{ color: COLORS.coverage }}>{coverage.pages_scanned}</div>
                <span className="text-[#9CA3AF]">{t('pages_scanned')}</span>
              </div>
            )}
            <TextBlock label={t('internal_linking')} value={coverage.internal_linking_assessment} />
            <TextBlock label={t('business_risk')} value={coverage.business_impact} />
          </div>

          {(coverage.content_gaps_count || coverage.content_gaps?.length) ? (
            <div className="mt-6 p-4 rounded-xl flex items-center gap-3" style={{ background: 'rgba(245,158,11,0.05)' }}>
              <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0" />
              <span className="text-sm text-[#D1D5DB]">{coverage.content_gaps_count ?? coverage.content_gaps?.length} {t('content_gaps')}.</span>
            </div>
          ) : null}
        </CardContent>
      </PremiumCard>

      {/* ── CONTENT ── */}
      <PremiumCard glowColor={COLORS.content}>
        <CardContent className="p-4 sm:p-10">
          <SectionHeader icon={FileText} title={t('content')} score={content.score} accentColor={COLORS.content} />
          
          <div className="space-y-6">
            <TextBlock label={t('structure_assessment')} value={content.structure_assessment} />
            <TextBlock label={t('conversion_risk')} value={content.conversion_impact} />
          </div>

          <div className="grid grid-cols-2 gap-4 mt-6">
            <div className="p-4 rounded-xl" style={{ background: 'rgba(34,197,94,0.05)' }}>
              <div className="text-2xl font-bold text-emerald-400">{content.expertise_signals_count ?? content.expertise_signals?.length ?? '—'}</div>
              <div className="text-xs text-[#9CA3AF] mt-1">{t('expertise_signals')}</div>
            </div>
            <div className="p-4 rounded-xl" style={{ background: 'rgba(245,158,11,0.05)' }}>
              <div className="text-2xl font-bold text-amber-400">{content.weaknesses_count ?? content.weaknesses?.length ?? '—'}</div>
              <div className="text-xs text-[#9CA3AF] mt-1">{t('weaknesses_detected')}</div>
            </div>
          </div>

          {content.section_note && (
            <p className="text-xs text-[#6B7280] mt-4 italic">{content.section_note}</p>
          )}
        </CardContent>
      </PremiumCard>

      {/* ── TRUST ── */}
      <PremiumCard glowColor={COLORS.trust}>
        <CardContent className="p-4 sm:p-10">
          <SectionHeader icon={Shield} title={t('trust')} score={trust.score} accentColor={COLORS.trust} />
          
          <TextBlock label={t('business_risk')} value={trust.commercial_risk} className="mb-6" />
          <TextBlock label={t('regional_assessment')} value={trust.region_specific_assessment} />

          <div className="grid grid-cols-2 gap-4 mt-6">
            <div className="p-4 rounded-xl" style={{ background: 'rgba(34,197,94,0.05)' }}>
              <div className="text-2xl font-bold text-emerald-400">{trust.detected_trust_elements_count ?? trust.detected_trust_elements?.length ?? '—'}</div>
              <div className="text-xs text-[#9CA3AF] mt-1">{t('trust_detected')}</div>
            </div>
            <div className="p-4 rounded-xl" style={{ background: 'rgba(239,68,68,0.05)' }}>
              <div className="text-2xl font-bold text-red-400">{trust.missing_trust_elements_count ?? trust.missing_trust_elements?.length ?? '—'}</div>
              <div className="text-xs text-[#9CA3AF] mt-1">{t('trust_missing')}</div>
            </div>
          </div>

          {trust.section_note && (
            <p className="text-xs text-[#6B7280] mt-4 italic">{trust.section_note}</p>
          )}
        </CardContent>
      </PremiumCard>

      {/* ── SEO ── */}
      <PremiumCard glowColor={COLORS.seo}>
        <CardContent className="p-4 sm:p-10">
          <SectionHeader icon={Search} title={t('seo')} score={seo.score} accentColor={COLORS.seo} />
          
          <div className="space-y-6">
            <TextBlock label={t('on_page_assessment')} value={seo.on_page_assessment} />
            <TextBlock label={t('technical_assessment')} value={seo.technical_assessment} />
            <TextBlock label={t('keyword_positioning')} value={seo.keyword_positioning_comment} />
            <TextBlock label={t('expected_visibility')} value={seo.expected_visibility_effect} />
          </div>

          {(seo.problems_count || seo.on_page_findings?.length || seo.technical_findings?.length) && (
            <div className="mt-6 p-4 rounded-xl flex items-center gap-3" style={{ background: 'rgba(245,158,11,0.05)' }}>
              <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0" />
              <span className="text-sm text-[#D1D5DB]">
                {seo.problems_count ?? ((seo.on_page_findings?.length || 0) + (seo.technical_findings?.length || 0))} {t('issues_detected')}.
                {seo.section_note ? ` ${seo.section_note}` : ''}
              </span>
            </div>
          )}
        </CardContent>
      </PremiumCard>

      {/* ── INDEXING ── */}
      <PremiumCard glowColor={COLORS.indexing}>
        <CardContent className="p-4 sm:p-10">
          <SectionHeader icon={BarChart2} title={t('indexing')} score={indexing.score} accentColor={COLORS.indexing} />
          
          {/* Overall Risk Summary - new or old structure */}
          {(indexing.overall_risk || indexing.indexing_risk) && (
            <div className="mb-6 p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)' }}>
              <p className="text-[15px] sm:text-[16px] text-[#D1D5DB]">{indexing.overall_risk || indexing.indexing_risk}</p>
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Sitemap - support both old and new structure */}
            {(() => {
              const status = indexing.sitemap?.status || (indexing.sitemap_status === 'yes' ? 'present' : indexing.sitemap_status === 'no' ? 'missing' : 'not_found');
              const comment = indexing.sitemap?.comment;
              return (
                <div className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)' }}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-[#9CA3AF] uppercase tracking-wider">{t('sitemap_label')}</span>
                    {status === 'present' ? (
                      <span className="flex items-center gap-1 text-xs text-emerald-400"><CheckCircle className="w-3 h-3" /> {t('present')}</span>
                    ) : status === 'missing' ? (
                      <span className="flex items-center gap-1 text-xs text-red-400"><XCircle className="w-3 h-3" /> {t('missing')}</span>
                    ) : (
                      <span className="flex items-center gap-1 text-xs text-amber-400"><AlertTriangle className="w-3 h-3" /> {t('not_detected')}</span>
                    )}
                  </div>
                  {comment ? (
                    <p className="text-[14px] sm:text-[15px] text-[#9CA3AF]">{comment}</p>
                  ) : status === 'not_found' && (
                    <p className="text-[14px] sm:text-[15px] text-[#6B7280]">Sitemap.xml was not detected during crawl. Consider adding one to improve search engine indexing.</p>
                  )}
                </div>
              );
            })()}

            {/* Robots.txt - support both old and new structure */}
            {(() => {
              const status = indexing.robots?.status || (indexing.robots_status === 'yes' ? 'present' : indexing.robots_status === 'no' ? 'missing' : 'not_found');
              const comment = indexing.robots?.comment;
              return (
                <div className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)' }}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-[#9CA3AF] uppercase tracking-wider">{t('robots_txt_label')}</span>
                    {status === 'present' ? (
                      <span className="flex items-center gap-1 text-xs text-emerald-400"><CheckCircle className="w-3 h-3" /> {t('present')}</span>
                    ) : status === 'missing' ? (
                      <span className="flex items-center gap-1 text-xs text-red-400"><XCircle className="w-3 h-3" /> {t('missing')}</span>
                    ) : (
                      <span className="flex items-center gap-1 text-xs text-amber-400"><AlertTriangle className="w-3 h-3" /> {t('not_detected')}</span>
                    )}
                  </div>
                  {comment ? (
                    <p className="text-[14px] sm:text-[15px] text-[#9CA3AF]">{comment}</p>
                  ) : status === 'not_found' && (
                    <p className="text-[14px] sm:text-[15px] text-[#6B7280]">Robots.txt was not detected.</p>
                  )}
                </div>
              );
            })()}

            {/* Canonical - support both old and new structure */}
            {(() => {
              const status = indexing.canonical?.status || (indexing.canonical_status === 'ok' ? 'ok' : indexing.canonical_status === 'issues' ? 'issues' : 'not_found');
              const comment = indexing.canonical?.comment;
              return (
                <div className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)' }}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-[#9CA3AF] uppercase tracking-wider">{t('canonical_tags')}</span>
                    {status === 'ok' ? (
                      <span className="flex items-center gap-1 text-xs text-emerald-400"><CheckCircle className="w-3 h-3" /> {t('ok_label')}</span>
                    ) : status === 'issues' ? (
                      <span className="flex items-center gap-1 text-xs text-amber-400"><AlertTriangle className="w-3 h-3" /> {t('issues_label')}</span>
                    ) : (
                      <span className="flex items-center gap-1 text-xs text-[#6B7280]"><AlertTriangle className="w-3 h-3" /> {t('not_detected')}</span>
                    )}
                  </div>
                  {comment ? (
                    <p className="text-[14px] sm:text-[15px] text-[#9CA3AF]">{comment}</p>
                  ) : status === 'issues' && (
                    <p className="text-[14px] sm:text-[15px] text-[#6B7280]">Canonical tag issues detected. This may cause duplicate content problems.</p>
                  )}
                </div>
              );
            })()}

            {/* Meta Robots - new structure only */}
            {(() => {
              const status = indexing.meta_robots?.status;
              const comment = indexing.meta_robots?.comment;
              if (!status && !comment) {
                return (
                  <div className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)' }}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-[#9CA3AF] uppercase tracking-wider">{t('meta_robots')}</span>
                      <span className="flex items-center gap-1 text-xs text-emerald-400"><CheckCircle className="w-3 h-3" /> {t('ok_label')}</span>
                    </div>
                    <p className="text-[14px] sm:text-[15px] text-[#6B7280]">{t('no_blocking_meta')}</p>
                  </div>
                );
              }
              return (
                <div className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)' }}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-[#9CA3AF] uppercase tracking-wider">{t('meta_robots')}</span>
                    {status === 'ok' ? (
                      <span className="flex items-center gap-1 text-xs text-emerald-400"><CheckCircle className="w-3 h-3" /> {t('ok_label')}</span>
                    ) : status === 'issues' ? (
                      <span className="flex items-center gap-1 text-xs text-amber-400"><AlertTriangle className="w-3 h-3" /> {t('issues_label')}</span>
                    ) : (
                      <span className="flex items-center gap-1 text-xs text-[#6B7280]"><AlertTriangle className="w-3 h-3" /> {t('not_detected')}</span>
                    )}
                  </div>
                  {comment && <p className="text-[14px] sm:text-[15px] text-[#9CA3AF]">{comment}</p>}
                </div>
              );
            })()}
          </div>
        </CardContent>
      </PremiumCard>

      {/* ── AI READINESS ── */}
      <PremiumCard glowColor={COLORS.ai_readiness}>
        <CardContent className="p-4 sm:p-10">
          <SectionHeader icon={Brain} title={t('ai_readiness')} score={ai_readiness.score} accentColor={COLORS.ai_readiness} />
          
          <div className="space-y-6">
            <TextBlock label={t('schema_detected')} value={ai_readiness.schema_detected} />
            <TextBlock label={t('citability_strength')} value={ai_readiness.citability_strength} />
            <TextBlock label={t('geo_alignment')} value={ai_readiness.geo_llm_alignment} />
            <TextBlock label={t('business_opportunity')} value={ai_readiness.business_opportunity} />
          </div>

          <div className="grid grid-cols-2 gap-4 mt-6">
            <div className="p-4 rounded-xl" style={{ background: `${COLORS.ai_readiness}08` }}>
              <div className="text-2xl font-bold" style={{ color: COLORS.ai_readiness }}>{ai_readiness.problems_count ?? ai_readiness.missing_signals_count ?? ai_readiness.missing_llm_signals?.length ?? '—'}</div>
              <div className="text-xs text-[#9CA3AF] mt-1">{t('missing_ai_signals')}</div>
            </div>
            <div className="p-4 rounded-xl" style={{ background: 'rgba(34,197,94,0.05)' }}>
              <div className="text-sm font-medium text-[#D1D5DB] mt-1">{ai_readiness.citability_level || ai_readiness.citability_strength || '—'}</div>
              <div className="text-xs text-[#9CA3AF] mt-1">{t('citability_level')}</div>
            </div>
          </div>

          {ai_readiness.general_assessment && (
            <TextBlock label="" value={ai_readiness.general_assessment} className="mt-4" />
          )}

          {ai_readiness.section_note && (
            <p className="text-xs text-[#6B7280] mt-4 italic">{ai_readiness.section_note}</p>
          )}

          {/* Market Intelligence — under AI Readiness */}
          {market_intelligence && market_intelligence.identified_entity && (
            <div className="mt-8">
              <MarketIntelligenceSection mi={market_intelligence} language={language} />
            </div>
          )}

          {/* GEO AI Visibility Check — under AI Readiness */}
          {geoCheck && geoCheck.questions?.length > 0 && (
            <div className="mt-6">
              <GeoVisibilitySection geoCheck={geoCheck} language={language} />
            </div>
          )}
        </CardContent>
      </PremiumCard>

      {/* ── COMPETITIVE ANALYSIS ── */}
      {competitive?.enabled && (
        <PremiumCard glowColor="#EC4899">
          <CardContent className="p-4 sm:p-10">
            <SectionHeader icon={Users} title={t('competitive_analysis')} accentColor="#EC4899" />
            
            {/* Comparison Table */}
            {competitive.comparison_table?.length > 0 && (
              <div className="mb-8">
                <div className="text-xs text-[#9CA3AF] uppercase tracking-wider mb-4">{t('score_comparison')}</div>
                <div className="overflow-x-auto -mx-4 sm:mx-0">
                  <table className="w-full min-w-[600px]">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left py-3 px-4 text-xs text-[#9CA3AF] uppercase tracking-wider font-medium">Domain</th>
                        <th className="text-center py-3 px-2 text-xs text-[#9CA3AF] uppercase tracking-wider font-medium">Pages</th>
                        <th className="text-center py-3 px-2 text-xs text-[#9CA3AF] uppercase tracking-wider font-medium">SEO</th>
                        <th className="text-center py-3 px-2 text-xs text-[#9CA3AF] uppercase tracking-wider font-medium">AI Ready</th>
                        <th className="text-center py-3 px-2 text-xs text-[#9CA3AF] uppercase tracking-wider font-medium">Trust</th>
                        <th className="text-center py-3 px-2 text-xs text-[#9CA3AF] uppercase tracking-wider font-medium">Schema %</th>
                        <th className="text-center py-3 px-2 text-xs text-[#9CA3AF] uppercase tracking-wider font-medium">FAQ %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {competitive.comparison_table.map((row, i) => {
                        const isTarget = i === 0;
                        return (
                          <tr 
                            key={i} 
                            className={`border-b border-white/5 ${isTarget ? 'bg-pink-500/5' : ''}`}
                          >
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2">
                                {isTarget && <Star className="w-4 h-4 text-pink-400 shrink-0" />}
                                <span className={`text-sm ${isTarget ? 'text-white font-medium' : 'text-[#9CA3AF]'}`}>
                                  {row.domain}
                                </span>
                              </div>
                            </td>
                            <td className="text-center py-3 px-2 text-sm text-[#D1D5DB]">{row.pages_analyzed}</td>
                            <td className="text-center py-3 px-2">
                              <span className="text-sm font-medium" style={{ color: scoreColorHex(row.seo_score) }}>
                                {row.seo_score}
                              </span>
                            </td>
                            <td className="text-center py-3 px-2">
                              <span className="text-sm font-medium" style={{ color: scoreColorHex(row.ai_readiness_score) }}>
                                {row.ai_readiness_score}
                              </span>
                            </td>
                            <td className="text-center py-3 px-2">
                              <span className="text-sm font-medium" style={{ color: scoreColorHex(row.trust_score) }}>
                                {row.trust_score}
                              </span>
                            </td>
                            <td className="text-center py-3 px-2 text-sm text-[#D1D5DB]">
                              {row.schema_presence_percent?.toFixed(0) || 0}%
                            </td>
                            <td className="text-center py-3 px-2 text-sm text-[#D1D5DB]">
                              {row.faq_pages_percent?.toFixed(0) || 0}%
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Wins & Losses */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Where You Win */}
              {competitive.wins_losses?.target_wins?.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wider mb-4 flex items-center gap-2">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                    <span className="text-emerald-400">{t('where_you_win')}</span>
                  </div>
                  <div className="space-y-3">
                    {competitive.wins_losses.target_wins.map((win, i) => (
                      <div 
                        key={i}
                        className="p-4 rounded-xl"
                        style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.15)' }}
                      >
                        <div className="flex items-start gap-3">
                          <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                          <div>
                            <h5 className="text-sm font-medium text-white mb-1">{win.topic}</h5>
                            <p className="text-[14px] text-[#9CA3AF]">{win.why_you_win}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Where You Lose */}
              {competitive.wins_losses?.target_losses?.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wider mb-4 flex items-center gap-2">
                    <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                    <span className="text-red-400">{t('where_competitors_lead')}</span>
                  </div>
                  <div className="space-y-3">
                    {competitive.wins_losses.target_losses.map((loss, i) => (
                      <div 
                        key={i}
                        className="p-4 rounded-xl"
                        style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.15)' }}
                      >
                        <div className="flex items-start gap-3">
                          <XCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                          <div>
                            <h5 className="text-sm font-medium text-white mb-1">
                              {loss.topic} <span className="text-red-400">vs {loss.competitor_domain}</span>
                            </h5>
                            <p className="text-[14px] text-[#9CA3AF] mb-2">{loss.why_they_win}</p>
                            {loss.recommended_fix && (
                              <div className="mt-2 p-2 rounded-lg" style={{ background: 'rgba(245,158,11,0.1)' }}>
                                <p className="text-xs text-amber-400 flex items-start gap-2">
                                  <Lightbulb className="w-3 h-3 shrink-0 mt-0.5" />
                                  <span><strong>Fix:</strong> {loss.recommended_fix.what_to_do}</span>
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Gaps */}
            {competitive.gaps?.length > 0 && (
              <div className="mb-8">
                <div className="text-xs text-[#9CA3AF] uppercase tracking-wider mb-4">{t('competitive_gaps')}</div>
                <div className="space-y-3">
                  {competitive.gaps.map((gap, i) => (
                    <div 
                      key={i}
                      className="p-4 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm text-white font-medium">{gap.metric}</span>
                          <span className="text-xs px-2 py-0.5 rounded" style={{ background: 'rgba(236,72,153,0.15)', color: '#EC4899' }}>
                            vs {gap.competitor_domain}
                          </span>
                        </div>
                        <p className="text-[13px] text-[#9CA3AF]">{gap.interpretation}</p>
                      </div>
                      <div className="flex items-center gap-4 shrink-0">
                        <div className="text-center">
                          <div className="text-xs text-[#6B7280]">You</div>
                          <div className="text-sm font-medium text-white">{typeof gap.you === 'number' ? fmtNum(gap.you) : gap.you}</div>
                        </div>
                        <ArrowRight className="w-4 h-4 text-[#6B7280]" />
                        <div className="text-center">
                          <div className="text-xs text-[#6B7280]">Them</div>
                          <div className="text-sm font-medium text-pink-400">{typeof gap.them === 'number' ? fmtNum(gap.them) : gap.them}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Opportunity Pages */}
            {competitive.opportunity_pages?.length > 0 && (
              <div>
                <div className="text-xs text-[#9CA3AF] uppercase tracking-wider mb-4">{t('suggested_pages')}</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {competitive.opportunity_pages.map((page, i) => (
                    <div 
                      key={i}
                      className="p-4 rounded-xl"
                      style={{ background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.15)' }}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Target className="w-4 h-4 text-blue-400" />
                        <span className="text-sm font-medium text-white">{page.page_type}</span>
                      </div>
                      <p className="text-xs font-mono text-blue-400 mb-2">{page.suggested_url}</p>
                      <p className="text-[13px] text-[#9CA3AF]">{page.why}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Notes */}
            {competitive.notes?.length > 0 && (
              <p className="text-xs text-[#6B7280] mt-6 italic">{competitive.notes.join(' ')}</p>
            )}
          </CardContent>
        </PremiumCard>
      )}

      {/* ── PROBLEMS & FIXES ── */}
      {problems_and_fixes?.length > 0 && (
        <PremiumCard glowColor="#EF4444">
          <CardContent className="p-4 sm:p-10">
            <SectionHeader icon={AlertTriangle} title={t('problems_and_fixes')} accentColor="#EF4444" />

            <div className="space-y-4">
              {problems_and_fixes.map((prob, i) => (
                <div
                  key={i}
                  className="p-4 sm:p-5 rounded-xl"
                  style={{
                    background: prob.severity === 'critical' ? 'rgba(239,68,68,0.06)' : prob.severity === 'high' ? 'rgba(245,158,11,0.06)' : 'rgba(107,114,128,0.06)',
                    border: `1px solid ${prob.severity === 'critical' ? 'rgba(239,68,68,0.15)' : prob.severity === 'high' ? 'rgba(245,158,11,0.15)' : 'rgba(107,114,128,0.15)'}`
                  }}
                >
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 mb-2">
                    <h4 className="text-sm font-semibold text-white">{prob.title}</h4>
                    <div className="flex gap-2 shrink-0">
                      <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${prob.severity === 'critical' ? 'bg-red-500/20 text-red-400 border border-red-500/30' : prob.severity === 'high' ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 'bg-zinc-500/20 text-zinc-400 border border-zinc-500/30'}`}>
                        {translateSeverity(prob.severity, language)}
                      </span>
                      <span className="text-xs px-2.5 py-1 rounded-full bg-white/5 text-zinc-400 border border-white/10">{translateCategory(prob.category, language)}</span>
                    </div>
                  </div>
                  {prob.url && prob.url !== 'site-wide' && <p className="text-xs text-blue-400 break-all mb-2">{prob.url}</p>}
                  {prob.evidence && <p className="text-sm text-[#9CA3AF] mb-2">{prob.evidence}</p>}
                  {prob.business_risk && <p className="text-sm text-red-300/80 mb-3">{prob.business_risk}</p>}
                  <ProblemFixDisplay fix={prob.fix} canViewCodeFixes={canViewCodeFixes} t={t} />
                </div>
              ))}
            </div>
          </CardContent>
        </PremiumCard>
      )}

      {/* ── ACTIONS ── */}
      <PremiumCard glowColor="#F59E0B">
        <CardContent className="p-4 sm:p-10">
          <SectionHeader icon={Zap} title={t('action_plan')} accentColor="#F59E0B" />

          {/* MI + GEO action items only (strategy moved to AI Readiness) */}
          {(miActions?.length > 0 || geoCheck) && (
            <ActionPlanSection
              actionPlan={actionPlan}
              auditId={auditId}
              canViewCodeFixes={canViewCodeFixes}
              marketIntelligence={null}
              miActions={miActions}
              geoCheck={null}
              language={language}
            />
          )}

          <div className="space-y-10">
            {/* P1 */}
            {actions.priority_1?.actions?.length > 0 && (
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <Flame className="w-5 h-5 text-red-400" />
                  <span className="text-sm font-semibold text-red-400 uppercase tracking-wider">
                    Priority 1 — {actions.priority_1.timeframe}
                  </span>
                </div>
                <div className="space-y-4">
                  {actions.priority_1.actions.map((action, i) => (
                    <div 
                      key={i} 
                      className="p-4 sm:p-5 rounded-xl"
                      style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)' }}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 sm:gap-4 mb-3">
                        <h4 className="text-sm font-semibold text-white">{action.task}</h4>
                        <div className="flex gap-2 shrink-0">
                          {action.impact_level && <ImpactBadge level={action.impact_level} />}
                          {action.effort_level && <EffortBadge level={action.effort_level} />}
                        </div>
                      </div>
                      {action.why && <p className="text-[15px] text-[#9CA3AF] mb-2">{action.why}</p>}
                      {action.expected_kpi_effect && (
                        <p className="text-[15px] flex items-center gap-2" style={{ color: '#22C55E' }}>
                          <TrendingUp className="w-4 h-4 shrink-0" /> {action.expected_kpi_effect}
                        </p>
                      )}
                      {action.kpi_ids_affected?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-3">
                          {action.kpi_ids_affected.map((kpi, j) => (
                            <span key={j} className="text-[10px] px-2 py-0.5 rounded bg-white/5 text-[#6B7280]">{kpi}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* P2 */}
            {actions.priority_2?.actions?.length > 0 && (
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <Clock className="w-5 h-5" style={{ color: '#F59E0B' }} />
                  <span className="text-sm font-semibold uppercase tracking-wider" style={{ color: '#F59E0B' }}>
                    Priority 2 — {actions.priority_2.timeframe}
                  </span>
                </div>
                <div className="space-y-4">
                  {actions.priority_2.actions.map((action, i) => (
                    <div 
                      key={i} 
                      className="p-4 sm:p-5 rounded-xl"
                      style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.15)' }}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 sm:gap-4 mb-3">
                        <h4 className="text-sm font-semibold text-white">{action.task}</h4>
                        <div className="flex gap-2 shrink-0">
                          {action.impact_level && <ImpactBadge level={action.impact_level} />}
                          {action.effort_level && <EffortBadge level={action.effort_level} />}
                        </div>
                      </div>
                      {action.why && <p className="text-[15px] text-[#9CA3AF] mb-2">{action.why}</p>}
                      {action.expected_kpi_effect && (
                        <p className="text-[15px] flex items-center gap-2" style={{ color: '#22C55E' }}>
                          <TrendingUp className="w-4 h-4 shrink-0" /> {action.expected_kpi_effect}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* P3 */}
            {actions.priority_3?.actions?.length > 0 && (
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <TrendingUp className="w-5 h-5" style={{ color: COLORS.overview }} />
                  <span className="text-sm font-semibold uppercase tracking-wider" style={{ color: COLORS.overview }}>
                    Priority 3 — {actions.priority_3.timeframe}
                  </span>
                </div>
                <div className="space-y-4">
                  {actions.priority_3.actions.map((action, i) => (
                    <div 
                      key={i} 
                      className="p-4 sm:p-5 rounded-xl"
                      style={{ background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.15)' }}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 sm:gap-4 mb-3">
                        <h4 className="text-sm font-semibold text-white">{action.task}</h4>
                        <div className="flex gap-2 shrink-0">
                          {action.impact_level && <ImpactBadge level={action.impact_level} />}
                          {action.effort_level && <EffortBadge level={action.effort_level} />}
                        </div>
                      </div>
                      {action.why && <p className="text-[15px] text-[#9CA3AF] mb-2">{action.why}</p>}
                      {action.expected_kpi_effect && (
                        <p className="text-[15px] flex items-center gap-2" style={{ color: '#22C55E' }}>
                          <TrendingUp className="w-4 h-4 shrink-0" /> {action.expected_kpi_effect}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Code fixes are shown inline per-problem/action item */}
          </div>
        </CardContent>
      </PremiumCard>

      {/* ── KPI METRICS (North Star + Metrics) ── */}
      {kpi.north_star && (kpi.north_star.value !== null || kpi.metrics?.length > 0) && (
        <PremiumCard glowColor="#10B981">
          <CardContent className="p-4 sm:p-10">
            <SectionHeader icon={Star} title={t('kpi_tracking')} accentColor="#10B981" />
            
            {/* North Star KPI - only show if has actual measurable value */}
            {kpi.north_star.value !== null && kpi.north_star.measurement_source !== 'not_available_from_crawl' && (
              <div 
                className="p-4 sm:p-6 rounded-xl mb-8"
                style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)' }}
              >
                <div className="text-xs text-emerald-400 uppercase tracking-wider mb-2">{t('north_star')} KPI</div>
                <h4 className="text-xl font-semibold text-white mb-2">{kpi.north_star.name}</h4>
                <p className="text-[15px] text-[#9CA3AF] mb-4">{kpi.north_star.definition}</p>
                
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                  <div>
                    <div className="text-xs text-[#6B7280]">Current</div>
                    <div className="text-lg font-semibold text-white">
                      {fmtNum(kpi.north_star.value) ?? 'N/A'} {kpi.north_star.unit}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-[#6B7280]">30-day Target</div>
                    <div className="text-lg font-semibold text-emerald-400">
                      {fmtNum(kpi.north_star.target_30d) ?? 'N/A'} {kpi.north_star.unit}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-[#6B7280]">90-day Target</div>
                    <div className="text-lg font-semibold text-emerald-400">
                      {fmtNum(kpi.north_star.target_90d) ?? 'N/A'} {kpi.north_star.unit}
                    </div>
                  </div>
                </div>
                
                {kpi.north_star.baseline_note && (
                  <p className="text-xs text-[#6B7280] mt-4">{kpi.north_star.baseline_note}</p>
                )}
              </div>
            )}

            {/* Other Metrics */}
            {kpi.metrics?.length > 0 && (
              <div>
                <div className="text-xs text-[#9CA3AF] uppercase tracking-wider mb-4">{t('additional_metrics')}</div>
                {/* 2 columns on mobile, 3 on lg */}
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  {kpi.metrics.slice(0, 12).map((metric, i) => (
                    <div 
                      key={i} 
                      className="p-4 rounded-xl"
                      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.04)' }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs px-2 py-0.5 rounded" style={{ background: 'rgba(255,255,255,0.05)', color: '#6B7280' }}>
                          {metric.category}
                        </span>
                        <span className="text-xs text-[#6B7280]">
                          {metric.confidence ? `${metric.confidence}% conf` : ''}
                        </span>
                      </div>
                      <h5 className="text-sm font-medium text-white mb-1">{metric.name}</h5>
                      <div className="text-xl font-semibold" style={{ color: COLORS.overview }}>
                        {fmtNum(metric.value) ?? 'N/A'} <span className="text-sm text-[#6B7280]">{metric.unit}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {kpi.notes && (
              <p className="text-xs text-[#6B7280] mt-6 italic">{kpi.notes}</p>
            )}
          </CardContent>
        </PremiumCard>
      )}

      {/* ═══════════════════ ALL URLs ANALYZED ═══════════════════ */}
      {crawledUrls && crawledUrls.length > 0 && (
        <PremiumCard className="mx-0">
          <CardContent className="p-5 sm:p-8">
            <SectionHeader icon={Globe} title={t('all_urls_analyzed')} accentColor="#6B7280" />
            <p className="text-[#6B7280] text-sm mb-4">{crawledUrls.length} {t('pages_crawled')}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-0">
              {crawledUrls.map((url, i) => (
                <div 
                  key={i} 
                  className="py-1.5 border-b border-white/[0.04] flex items-baseline gap-2 text-sm overflow-hidden"
                >
                  <span className="text-[#4B5563] text-xs shrink-0 w-6 text-right">{i + 1}.</span>
                  <a 
                    href={url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-[#9CA3AF] hover:text-blue-400 truncate transition-colors"
                    data-testid={`crawled-url-${i}`}
                  >
                    {url.replace(/^https?:\/\//, '')}
                  </a>
                </div>
              ))}
            </div>
          </CardContent>
        </PremiumCard>
      )}

    </div>
  );
}
